/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-10-06
 */

/**
 * Color palette with 50-950 shades generated from a base color
 */
export interface ColorPalette {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string; // Base color
  600: string;
  700: string;
  800: string;
  900: string;
  950: string;
}

/**
 * Text colors for different contexts
 */
export interface TextColors {
  primary: string;
  secondary: string;
  inverse: string; // Text on dark backgrounds
}

/**
 * Base theme colors (user-selected)
 */
export interface BaseThemeColors {
  primary: string;
  secondary: string;
  neutral: string;
  success: string;
  warning: string;
  error: string;
  background: string;
  surface: string;
  text: TextColors;
}

/**
 * Generated color palettes
 */
export interface GeneratedColorPalettes {
  primary: ColorPalette;
  secondary: ColorPalette;
  neutral: ColorPalette;
  success: ColorPalette;
  warning: ColorPalette;
  error: ColorPalette;
}

/**
 * Computed "on" colors for text on colored backgrounds
 */
export interface OnColors {
  onPrimary: string;
  onSecondary: string;
  onNeutral: string;
  onSuccess: string;
  onWarning: string;
  onError: string;
  onBackground: string;
  onSurface: string;
}

/**
 * Accessibility warning for color combinations
 */
export interface AccessibilityWarning {
  id: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
  foreground: string;
  background: string;
  contrastRatio: number;
  recommendation?: string;
}

/**
 * Contrast ratio data for all color combinations
 */
export interface ContrastRatios {
  textOnBackground: number;
  textOnSurface: number;
  onPrimaryOnPrimary: number;
  onSecondaryOnSecondary: number;
  onSuccessOnSuccess: number;
  onWarningOnWarning: number;
  onErrorOnError: number;
  [key: string]: number;
}

/**
 * Accessibility validation results
 */
export interface AccessibilityValidation {
  contrastRatios: ContrastRatios;
  warnings: AccessibilityWarning[];
  wcagAACompliant: boolean;
  wcagAAACompliant: boolean;
  overallScore: number; // 0-100
}

/**
 * Typography scale configuration
 */
export interface TypographyScale {
  fontFamily: {
    body: string;
    heading: string;
    accent: string;
  };
  fontSizeScale: {
    xs: number;
    sm: number;
    md: number; // Base size
    lg: number;
    xl: number;
    xxl: number;
  };
  lineHeights: {
    tight: number;
    normal: number;
    relaxed: number;
  };
  fontWeights: {
    normal: number;
    medium: number;
    semibold: number;
    bold: number;
  };
}

/**
 * Shape and spacing configuration
 */
export interface ShapeAndSpacing {
  radiusScale: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
  };
  spacingScale: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
    xxl: number;
  };
  shadowScale: {
    sm: string;
    md: string;
    lg: string;
  };
}

/**
 * Complete theme token structure
 */
export interface ThemeTokens {
  colors: BaseThemeColors;
  typography: TypographyScale;
  shape: ShapeAndSpacing;
  modes: {
    light: boolean;
    dark: boolean;
  };
}

/**
 * Generated theme data (computed from tokens)
 */
export interface GeneratedThemeData {
  palettes: GeneratedColorPalettes;
  onColors: OnColors;
  accessibility: AccessibilityValidation;
  muiTheme: Record<string, any>; // MUI theme object
  cssVariables: Record<string, string>; // CSS custom properties
}

/**
 * Complete studio theme
 */
export interface StudioTheme {
  id: string;
  studioId: string;
  name: string;
  status: 'draft' | 'published' | 'archived';
  version: number;
  tokens: ThemeTokens;
  generated: GeneratedThemeData;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  publishedBy?: string;
}

/**
 * Theme preset definition
 */
export interface ThemePreset {
  id: string;
  name: string;
  description: string;
  category: 'dance-style' | 'industry' | 'mood' | 'custom';
  tokens: ThemeTokens;
  preview: {
    primaryColor: string;
    secondaryColor: string;
    backgroundImage?: string;
  };
}

/**
 * Color picker component props
 */
export interface ColorPickerValue {
  hex: string;
  rgb: { r: number; g: number; b: number };
  hsl: { h: number; s: number; l: number };
  hsv: { h: number; s: number; v: number };
}

/**
 * Theme editor state
 */
export interface ThemeEditorState {
  currentTheme: StudioTheme | null;
  isModified: boolean;
  isGenerating: boolean;
  isSaving: boolean;
  isPublishing: boolean;
  previewMode: 'light' | 'dark';
  selectedColorProperty: keyof BaseThemeColors | null;
  showAccessibilityPanel: boolean;
  showPresetsPanel: boolean;
}

/**
 * Default theme tokens (tools.dance brand)
 */
export const DEFAULT_THEME_TOKENS: ThemeTokens = {
  colors: {
    primary: '#1976d2',
    secondary: '#dc004e',
    neutral: '#5A5A5A',
    success: '#4CAF50',
    warning: '#FF9800',
    error: '#F44336',
    background: '#FAFAFA',
    surface: '#FFFFFF',
    text: {
      primary: '#1F1F1F',
      secondary: '#5A5A5A',
      inverse: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: {
      body: 'Arial, Helvetica, sans-serif',
      heading: 'Arial, Helvetica, sans-serif',
      accent: 'Arial, Helvetica, sans-serif',
    },
    fontSizeScale: {
      xs: 12,
      sm: 14,
      md: 16,
      lg: 18,
      xl: 20,
      xxl: 24,
    },
    lineHeights: {
      tight: 1.2,
      normal: 1.5,
      relaxed: 1.8,
    },
    fontWeights: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },
  shape: {
    radiusScale: {
      xs: 2,
      sm: 4,
      md: 8,
      lg: 12,
      xl: 16,
    },
    spacingScale: {
      xs: 4,
      sm: 8,
      md: 16,
      lg: 24,
      xl: 32,
      xxl: 48,
    },
    shadowScale: {
      sm: '0 1px 2px rgba(0,0,0,0.05)',
      md: '0 4px 6px rgba(0,0,0,0.07)',
      lg: '0 10px 15px rgba(0,0,0,0.1)',
    },
  },
  modes: {
    light: true,
    dark: false,
  },
};
